import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, DesktopLayout, LiveAnswer as SharedLiveAnswer, LoadingQuestion, TimerSection } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { getCloseOrNotBranding } from '../../utils/closeOrNotBranding';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface CloseOrNotQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  numericAnswer?: number;
  unit?: string;
  questionType?: string;
  hint?: string;
  isCloseOrNot?: boolean;
}

interface ClosenessRankEntry {
  playerId: string;
  playerName: string;
  rank: number;
  answer: number;
  difference: number;
  avatar?: string;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  numericAnswer?: number;
  unit?: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  closenessRanking?: ClosenessRankEntry[];
  isCloseOrNot?: boolean;
}

interface LiveAnswer {
  playerId: string;
  playerName: string;
  avatar?: string;
  timestamp: number;
  numericAnswer?: number;
}

interface CloseOrNotHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: CloseOrNotQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

// Theme colors for Close or Not
const THEME = {
  primary: '#F97316', // Orange
  primaryLight: 'rgba(249, 115, 22, 0.15)',
  primaryBorder: 'rgba(249, 115, 22, 0.3)',
  accent: '#34D399', // Green
  accentLight: 'rgba(52, 211, 153, 0.15)',
  accentBorder: 'rgba(52, 211, 153, 0.3)',
};

const CloseOrNotHostDisplay: React.FC<CloseOrNotHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  myPlayerId,
  hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
}) => {
  const { t } = useTranslation();
  const branding = getCloseOrNotBranding(roomState.gameType);
  const [currentQuestion, setCurrentQuestion] = useState<CloseOrNotQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);
  const [shouldUpdateStandings, setShouldUpdateStandings] = useState(false);

  // Host playing mode state
  const [answer, setAnswer] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [playerClosenessRank, setPlayerClosenessRank] = useState<number | null>(null);
  const [playerDifference, setPlayerDifference] = useState<number | null>(null);
  const [correctNumericAnswer, setCorrectNumericAnswer] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef<boolean>(false);

  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef<string>(roomState.roomName);
  const playersRef = useRef(roomState.players);

  const gameEndData = useGameEndScreenData(roomState);
  // Helper to reset host playing state
  const resetHostPlayingState = () => {
    setAnswer('');
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setPlayerClosenessRank(null);
    setPlayerDifference(null);
    setCorrectNumericAnswer(null);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
    if (hostIsPlaying) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  useEffect(() => {
    playersRef.current = roomState.players;
  }, [roomState.players]);

  // When server says game is finished (e.g. reconnection after missing game-ended), show game end screen
  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  }, [roomState.gameState, gameEndData]);

  // Update local state when roomState.currentQuestion changes
  useEffect(() => {
    if (roomState.gameState === 'finished') return;
    if (roomState.currentQuestion) {
      const newQuestionId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;

      // Use full timeLimit - user doesn't care about timer accuracy, just that game works
      const calculatedTimeRemaining = roomState.currentQuestion.timeLimit;

      // Update if it's a different question OR if we don't have a current question yet
      if (isNewQuestion || !currentQuestion) {
        if (isNewQuestion) {
          console.log(`CloseOrNotHostDisplay: New question. ID: ${newQuestionId}`);
        } else {
          console.log(`CloseOrNotHostDisplay: Refreshing question state (reconnection). ID: ${newQuestionId}, TimeRemaining: ${calculatedTimeRemaining}ms`);
        }
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(roomState.currentQuestion);
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionResult(null);
        // Reset standings update flag for new question
        setShouldUpdateStandings(false);
        // Only reset host playing state if it's a new question, not if we're just syncing mid-game or reconnecting
        if (isNewQuestion) {
          resetHostPlayingState();
        }
      }
    } else if (roomState.gameState === 'waiting') {
      setCurrentQuestion(null);
      setQuestionResult(null);
      setWaitingForNewGame(false);
      setGameEnded(false);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomState.currentQuestion, roomState.questionStartTime, roomState.gameState, hostIsPlaying, currentQuestion, timeRemaining]);

  useEffect(() => {
    const handleQuestion = (data: CloseOrNotQuestion) => {
      console.log('CloseOrNotHostDisplay received question:', data);
      const newQuestionId = data.questionId || data.id;

      // Use full timeLimit on reconnection - user doesn't care about timer accuracy, just that game works
      // This simplifies the logic and ensures the game continues to flow
      const calculatedTimeRemaining = data.timeLimit;

      if (newQuestionId !== previousQuestionIdRef.current) {
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(data);
        setQuestionResult(null);
        setTimeRemaining(calculatedTimeRemaining);
        resetHostPlayingState();
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      console.log('Question result:', data);
      setQuestionResult(data);
      // Trigger score update in StandingsSidebar
      setShouldUpdateStandings(true);
      // Reset after a short delay to allow the update to process
      setTimeout(() => {
        setShouldUpdateStandings(false);
      }, 100);
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
    };

    const handleGameStarted = () => {
      console.log('CloseOrNotHostDisplay: game-started');
      setWaitingForNewGame(false);
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setQuestionResult(null);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
      socket.emit('get-room-state', { roomName: roomNameRef.current });
    };

    // Handle answer-received for host playing mode
    const handleAnswerReceived = (data: {
      isCorrect: boolean;
      points: number;
      responseTime: number;
      closenessRank?: number | null;
      numericAnswer?: number;
      playerAnswer?: number;
      difference?: number;
      correctAnswer?: string;
    }) => {
      console.log('CloseOrNotHostDisplay answer-received:', data);
      isSubmittingRef.current = false;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      setPlayerClosenessRank(data.closenessRank || null);
      setCorrectNumericAnswer(data.numericAnswer || null);
      setPlayerDifference(data.difference !== undefined ? data.difference : null);
    };

    // Listen for live player answers (broadcast to room)
    const handlePlayerAnswered = (data: {
      playerId: string;
      playerName: string;
      avatar?: string;
      questionId: string;
      timestamp: number;
      answer?: number;
    }) => {
      // Don't show host's own answer in the live list
      if (data.playerId === myPlayerId) return;
      
      setLiveAnswers(prev => {
        if (prev.some(a => a.playerId === data.playerId)) return prev;
        return [...prev, {
          playerId: data.playerId,
          playerName: data.playerName,
          avatar: data.avatar,
          timestamp: data.timestamp,
          numericAnswer: data.answer,
        }];
      });
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('player-answered', handlePlayerAnswered);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('player-answered', handlePlayerAnswered);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, hostIsPlaying, myPlayerId]);

  // Timer countdown
  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !questionResult) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) return 0;
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, questionResult]);

  // Host submit answer
  const handleSubmitAnswer = () => {
    if (!answer.trim() || !currentQuestion || answerSubmitted || isSubmittingRef.current) {
      return;
    }

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('Question ID not found');
      return;
    }

    isSubmittingRef.current = true;
    setAnswerSubmitted(true);

    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: questionId,
      answer: answer.trim(),
      timestamp: Date.now(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  // Game ended screen
  if (gameEnded) {
    const handlePlayAnotherGame = () => {
      if (onBackToGameSelection) {
        onBackToGameSelection();
      } else {
        window.location.reload();
      }
    };

    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        setQuestionResult(null);
        setWaitingForNewGame(true);
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? [])}
        gameStatistics={gameStatistics ?? gameEndData?.gameStatistics ?? null}
        onPlaySameGame={handlePlaySameGame}
        onPlayAnotherGame={handlePlayAnotherGame}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  // Waiting for new game
  if (waitingForNewGame) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{branding.icon} {branding.title}</h1>
          <p className="subtitle">{t('gameplay.loading.startingNewGame')}</p>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '60px',
              height: '60px',
              border: `6px solid ${THEME.primaryBorder}`,
              borderTop: `6px solid ${THEME.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={`${branding.icon} ${branding.title}`}
        subtitle={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  const timerPercent = currentQuestion.timeLimit > 0 ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;

  const handleBackToGameSelection = () => {
    if (onBackToGameSelection) {
      onBackToGameSelection();
    }
  };

  const handleRestartGame = () => {
    if (socket && roomState) {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setFinalLeaderboard([]);
      setCurrentQuestion(null);
      setQuestionResult(null);
      resetHostPlayingState();
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  // Get closeness feedback for host playing mode
  const getClosenessFeedback = (): { emoji: string; text: string; color: string } => {
    if (playerDifference === null || correctNumericAnswer === null) {
      return { emoji: '🤔', text: 'Invalid answer', color: '#EF4444' };
    }

    const percentDiff = correctNumericAnswer !== 0
      ? (playerDifference / Math.abs(correctNumericAnswer)) * 100
      : playerDifference;

    if (playerDifference === 0) {
      return { emoji: '🎯', text: 'PERFECT!', color: '#22C55E' };
    } else if (percentDiff <= 1) {
      return { emoji: '🔥', text: 'So Close!', color: '#22C55E' };
    } else if (percentDiff <= 5) {
      return { emoji: '✨', text: 'Very Close!', color: '#22C55E' };
    } else if (percentDiff <= 10) {
      return { emoji: '👍', text: 'Pretty Close!', color: '#84CC16' };
    } else if (percentDiff <= 25) {
      return { emoji: '😅', text: 'Not Bad!', color: '#F59E0B' };
    } else if (percentDiff <= 50) {
      return { emoji: '😬', text: 'Getting There...', color: '#F97316' };
    } else {
      return { emoji: '😵', text: 'Way Off!', color: '#EF4444' };
    }
  };

  // Convert liveAnswers to shared format for StandingsSidebar
  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(la => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.numericAnswer !== undefined ? la.numericAnswer.toLocaleString() : undefined,
  }));

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: handleBackToGameSelection,
          tooltip: t('gameplay.backToGameSelection')
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRestartGame,
          tooltip: t('gameplay.restartGame')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
                {branding.icon} {branding.shortTitle}
              </span>
            </div>
            <div className="header-game-name" style={{ 
              width: '1px', 
              height: '24px', 
              backgroundColor: 'rgba(255, 255, 255, 0.3)' 
            }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{ 
                fontSize: '1.3rem', 
                fontWeight: 'bold', 
                letterSpacing: '0.15rem',
                color: THEME.primary,
                padding: '0.3rem 0.8rem',
                backgroundColor: THEME.primaryLight,
                borderRadius: '6px',
                border: `2px solid ${THEME.primaryBorder}`,
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      {/* Main game layout - desktop: left (question) | right (standings), mobile: stacked */}
      <div style={{
        minHeight: 'calc(100vh - 82px)',
        width: '100%',
        padding: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}>
        <DesktopLayout
          leftContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Main Challenge Display: timer-section (full width) + game-display-centered-content (centered) as siblings */}
        <div
          className="container"
          style={{
            paddingTop: '1rem',
            paddingBottom: '1rem',
            marginTop: '1rem',
            marginBottom: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          {/* Sibling 1: timer section – full width */}
          <div className="timer-section" style={{ width: '100%', marginBottom: '1.2rem' }}>
            <TimerSection
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              timeSeconds={Math.ceil(timeRemaining / 1000)}
              progress={timerPercent}
              primaryColor={timerPercent > 50 ? THEME.primary : timerPercent > 20 ? '#F59E0B' : undefined}
            />
          </div>

          {/* Sibling 2: inner flex with align-items: center – question, correct answer, host input */}
          <div className="game-display-centered-content" style={{ flex: 1 }}>
          {/* Question text - using same font size as trivia game */}
          <h1 className="game-question-text" style={{
            color: '#F97316',
            marginBottom: '1.5rem',
            letterSpacing: '0.03em',
            textShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
          }}>
            {currentQuestion.question}
          </h1>
          
          {/* Unit hint if available */}
          {currentQuestion.unit && (
            <p style={{
              fontSize: '1.15rem',
              color: THEME.primary,
              textAlign: 'center',
              marginTop: '-1rem',
              marginBottom: '1.5rem',
            }}>
              Answer in: {currentQuestion.unit}
            </p>
          )}

          {/* Correct Answer - shown when results are in */}
          {questionResult && (
            <div style={{
              padding: '1.5rem',
              backgroundColor: THEME.accentLight,
              borderRadius: '15px',
              border: `2px solid ${THEME.accent}`,
              textAlign: 'center',
              marginTop: '2rem',
            }}>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.25rem' }}>
                ✓ {t('gameplay.correctAnswerLabel')}
              </div>
              <div style={{
                fontSize: 'clamp(2.5rem, 7vw, 4rem)',
                fontWeight: 'bold',
                color: THEME.accent,
              }}>
                {questionResult.numericAnswer?.toLocaleString()}
                {questionResult.unit && (
                  <span style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.2rem)', marginLeft: '0.5rem' }}>{questionResult.unit}</span>
                )}
              </div>
            </div>
          )}

          {/* Host playing input */}
          {!questionResult && hostIsPlaying && !answerSubmitted && (
            <div style={{ 
              marginTop: '2rem', 
              width: '100%',
              maxWidth: '600px',
              margin: '2rem auto 0',
            }}>
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('gameplay.typeYourAnswer')}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '1.2rem',
                  fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                  fontFamily: 'monospace',
                  textAlign: 'center',
                  backgroundColor: 'rgba(248, 250, 252, 0.1)',
                  border: `3px solid ${THEME.primaryBorder}`,
                  borderRadius: '15px',
                  color: '#fff',
                  outline: 'none',
                }}
              />
              <button
                onClick={handleSubmitAnswer}
                disabled={!answer.trim()}
                className="button"
                style={{
                  width: '100%',
                  marginTop: '1rem',
                  backgroundColor: answer.trim() ? THEME.primary : 'rgba(248, 250, 252, 0.3)',
                  opacity: answer.trim() ? 1 : 0.5,
                  cursor: answer.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t('gameplay.submitAnswer')}
              </button>
            </div>
          )}

          {/* Host answer submitted feedback */}
          {!questionResult && hostIsPlaying && answerSubmitted && (
            <div style={{ 
              marginTop: '2rem', 
              width: '100%',
              maxWidth: '600px',
              margin: '2rem auto 0',
            }}>
              <div style={{
                padding: '1.5rem',
                backgroundColor: THEME.primaryLight,
                borderRadius: '15px',
                border: `3px solid ${getClosenessFeedback().color}`,
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
                  {getClosenessFeedback().emoji}
                </div>
                <div style={{
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  color: getClosenessFeedback().color,
                }}>
                  {getClosenessFeedback().text}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginTop: '0.75rem',
                }}>
                  {playerClosenessRank && (
                    <div style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: THEME.primaryLight,
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      color: '#fff',
                    }}>
                      {playerClosenessRank === 1 && '🥇 Closest!'}
                      {playerClosenessRank === 2 && '🥈 2nd Closest'}
                      {playerClosenessRank === 3 && '🥉 3rd Closest'}
                      {playerClosenessRank > 3 && `#${playerClosenessRank} Closest`}
                    </div>
                  )}
                  <div style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: pointsEarned > 0 ? THEME.accentLight : 'rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    color: pointsEarned > 0 ? THEME.accent : '#EF4444',
                  }}>
                    +{pointsEarned} pts
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={shouldUpdateStandings}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.accent,
                accentLight: THEME.accentLight,
                accentBorder: THEME.accentBorder,
              }}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={shouldUpdateStandings}
              isMobile={true}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.accent,
                accentLight: THEME.accentLight,
                accentBorder: THEME.accentBorder,
              }}
            />
          }
          rightWidth="320px"
        />
      </div>

    </div>
  );
};

export default CloseOrNotHostDisplay;
